<template>
  <div class="personalized-discount relative">
    <div
      v-if="showPopup"
      class="absolute bottom-full -left-1 -right-1 bg-white shadow-lg border rounded
      p-2 z-10 text-xs flex gap-2 justify-between"
    >
      <i18n-t
      keypath="personalized-discount.popup-message">
        <template #expondoPartnersUrl>
          <CustomLink
            :url="expondoPartnersUrl"
          >
            expondo Partners
          </CustomLink>
        </template>
      </i18n-t>
      <CloseButton
        :small="true"
        :icon-color-class="'text-EXPONDO-primary'"
        @click="showPopup = false"
      />
    </div>
    <div
      class="p-2 cursor-pointer border text-sm"
      :class="discountColor"
      @click="togglePopup"
    >
      <div class="flex gap-2.5 flex-1">
        <div
          v-if="loyaltyPrice"
          class="flex items-center"
        >
          <!-- Here, already discounted loyalty price is passed as full-product-price
           because ProductPricing is repurposed here exceptionally for DRY purposes-->
          <ProductPricing
            :full-product-price="loyaltyPrice"
            :is-important-price="true"
            :size="PRODUCT_PRICING_SIZE.SMALL_PLUS"
          />
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <div class="flex justify-between gap-1">
            <DiscountCategoryLabel
              :category="DISCOUNT_CATEGORY.LOYALTY"
              :loyalty-level="loyaltyLevel"
            />
            <span class="iconify svg--info text-base text-EXPONDO-primary-60 shrink-0" />
          </div>

          <div class="flex flex-col">
            <strong v-if="!isLoggedIn">{{ $t('this-order-counts') }}</strong>
            <span>{{ $t(personalizedDiscountDescription, { discount_percent: loyaltyDiscountPercentage }) }}</span>
          </div>
          <CustomLink
            v-if="!isLoggedIn"
            :type="LINK_TYPE.Primary"
            :url="'/login'"
          >
            {{ $t('Login/Register') }}
          </CustomLink>
          <CustomLink
            v-else-if="!hasPersonalizedDiscount"
            :type="LINK_TYPE.Primary"
            :url="expondoPartnersUrl"
          >
            {{ $t('Learn more') }}
          </CustomLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">

import { INFORMATION_BOX_TYPE } from '~/components/atoms/InformationBox/InformationBoxType';

import { PRODUCT_PRICING_SIZE } from '~~/constants/productCardPricing';
import { getLoyaltyLevelNumber } from '~~/helpers/getLoyaltyLevelNumber';
import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import { DISCOUNT_CATEGORY } from '~~/types/discounts';
import { ROUTES } from '~~/constants/routes';

export default defineComponent({
  name: 'PersonalizedDiscount',

  props: {
    productPrice: {
      type: Number,
      required: false,
      default: null,
    },
  },
  setup(props) {

    const { customerTierKey, isLoggedIn } = useUserExtended();
    const { highestLoyaltyDiscountPercentage } = useCTLoyaltyLevels();
    const { getAdjustedSlug } = useRouteExtended();
    const {
      personalizedDiscount,
      personalizedPrice,
      hasPersonalizedDiscount,
    } = usePersonalizedDiscount(props.productPrice);

    const showPopup = ref(false);
    const expondoPartnersUrl = computed(() => getAdjustedSlug(ROUTES.EXPONDO_PARTNERS));

    const togglePopup = () => {

      showPopup.value = !showPopup.value;

    };

    const loyaltyLevel = computed(() => {

      if (!customerTierKey.value || !isLoggedIn.value) {

        return undefined;

      }

      return {
        levelNumber: getLoyaltyLevelNumber(customerTierKey.value),
      };

    });

    const loyaltyPrice = computed(() => {

      if (hasPersonalizedDiscount.value) {

        return personalizedPrice.value;

      }

      return null;

    });

    const loyaltyDiscountPercentage = computed(() => {

      if (!isLoggedIn.value) {

        return highestLoyaltyDiscountPercentage.value;

      }

      return personalizedDiscount.value;

    });

    const personalizedDiscountDescription = computed(() => {

      if (!isLoggedIn.value) {

        return 'personalized-discount.description.up-to-x-off';

      } else if (!hasPersonalizedDiscount.value) {

        return 'personalized-discount.description.start-shopping';

      } else {

        return 'personalized-discount.description.your-personalized-price';

      }

    });

    const discountColor = computed(() => {

      return hasPersonalizedDiscount.value
        ? 'bg-EXPONDO-green-extraLight border-EXPONDO-green'
        : 'bg-EXPONDO-secondary-light border-EXPONDO-secondary-medium';

    });

    return {
      INFORMATION_BOX_TYPE,
      LINK_TYPE,
      customerTierKey,
      personalizedDiscount,
      personalizedDiscountDescription,
      isLoggedIn,
      personalizedPrice,
      PRODUCT_PRICING_SIZE,
      hasPersonalizedDiscount,
      DISCOUNT_CATEGORY,
      loyaltyLevel,
      loyaltyPrice,
      loyaltyDiscountPercentage,
      showPopup,
      discountColor,
      togglePopup,
      expondoPartnersUrl,
    };

  },
});

</script>

<style lang="scss" scoped>
.personalized-discount {
  :deep(a) {
    line-height: inherit;
  }
}
</style>
